export function getAuditSlugFromResponse(data) {
  if (!data) return null;
  const nested = data.data && typeof data.data === "object" ? data.data : null;
  return (
    data.slug ??
    nested?.slug ??
    data.share_slug ??
    nested?.share_slug ??
    data.shareSlug ??
    data.share_token ??
    data.shareToken ??
    null
  );
}

export function unwrapTestingToolApiPayload(data) {
  if (!data) return null;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return { ...data, ...data.data };
  }
  return data;
}

export function getDisplayIssues(payload) {
  if (!payload) return [];

  // retrievedIssues is the full findings list — always prefer it.
  // finalIssues is a curated subset (often just 1 item); using it first
  // was silently discarding the rest of the issues list.
  const retrievedIssues =
    payload.retrievedIssues ?? payload.retrieved_issues ?? [];
  if (Array.isArray(retrievedIssues) && retrievedIssues.length > 0) {
    return retrievedIssues;
  }

  const deterministicIssues =
    payload.deterministicIssues ?? payload.deterministic_issues ?? [];
  if (Array.isArray(deterministicIssues) && deterministicIssues.length > 0) {
    return deterministicIssues;
  }

  const finalIssues = payload.finalIssues ?? payload.final_issues ?? [];
  if (Array.isArray(finalIssues) && finalIssues.length > 0) {
    return finalIssues;
  }

  // Fallback: APIs may store issues under generic keys
  const generic =
    payload.issues ?? payload.issue_cards ?? payload.findings ?? [];
  return Array.isArray(generic) ? generic : [];
}

export function parseTestingToolReportResponse(data) {
  const payload = unwrapTestingToolApiPayload(data) ?? data ?? {};
  const report = payload?.report ?? payload?.summary ?? "";
  const metadata = payload?.metadata ?? {};
  const displayIssues = getDisplayIssues(payload);

  const finalIssues = payload.finalIssues ?? payload.final_issues ?? [];
  const deterministicIssues =
    payload.deterministicIssues ?? payload.deterministic_issues ?? [];
  const rawRetrievedIssues =
    payload.retrievedIssues ?? payload.retrieved_issues ?? [];
  const suppressedIssues =
    payload.suppressedIssues ?? payload.suppressed_issues ?? [];

  const usedMock = Boolean(payload?.usedMock);
  const model = payload?.model ?? "";

  let message = payload?.message;
  if (!message) {
    if (usedMock) {
      message =
        payload?.note ??
        `Sample report${model ? ` (${model})` : ""}.`;
    } else {
      message = model ? `Live response via ${model}` : "Live response via Claude";
    }
  }

  return {
    report,
    retrievedIssues: displayIssues,
    finalIssues: Array.isArray(finalIssues) ? finalIssues : [],
    deterministicIssues: Array.isArray(deterministicIssues)
      ? deterministicIssues
      : [],
    rawRetrievedIssues: Array.isArray(rawRetrievedIssues)
      ? rawRetrievedIssues
      : [],
    suppressedIssues: Array.isArray(suppressedIssues) ? suppressedIssues : [],
    metadata,
    siteType: metadata?.siteType ?? payload?.site_type ?? "",
    metadataConfidence: metadata?.confidence ?? null,
    testTestingToolId:
      payload?.test_testing_tool_id ?? payload?.testTestingToolId ?? null,
    message,
    usedMock,
    model: payload?.model ?? null,
    usedCompany: payload?.usedCompany ?? null,
    totalEstimatedFixHours: (() => {
      if (typeof payload?.total_estimated_fix_hours === "number") {
        return payload.total_estimated_fix_hours;
      }
      // Fall back to summing per-issue hours when the top-level total is absent
      const sum = displayIssues.reduce((acc, issue) => {
        const h =
          issue?.estimated_fix_hours ??
          issue?.effort?.estimated_hours ??
          issue?.effort_hours ??
          0;
        return acc + (typeof h === "number" ? h : 0);
      }, 0);
      return sum > 0 ? sum : null;
    })(),
    totalOpenFindings: displayIssues.length,
    slug: getAuditSlugFromResponse(payload) ?? getAuditSlugFromResponse(data),
  };
}
